import React, { createContext, ReactNode } from 'react';
import { useExportState } from '@hooks/useExportState';
import { ExportStateData } from '@hooks/useExportState';
import { EntityTypeEnum, ExportColumnInDTO, ExportFormatEnum } from '@api/ringil-api.v2';

/**
 * ExportContext provides a single shared instance of export state
 * across all components in the application.
 * 
 * This solves the "separate hook instances" problem:
 * - Banner and button now access the SAME hook instance
 * - State changes are immediately visible to all components
 * - Storage listener still works for multi-tab sync
 */

type ExportContextType = {
  activeExports: Record<string, ExportStateData>;
  /** `columns`: export these columns (a v3 tab's) instead of the entity's fixed set; null = the fixed set, sent explicitly. */
  startExport: (entityType: EntityTypeEnum, entityLabel: string, filter: unknown, exportFormat: ExportFormatEnum, columns?: ExportColumnInDTO[] | null) => void;
  cancelExport: (jobId: string) => void;
  resetAll: () => void;
};

export const ExportContext = createContext<ExportContextType | null>(null);

interface ExportProviderProps {
  children: ReactNode;
}

/**
 * ExportProvider - Wrap your app with this to provide export state to all children
 * 
 * Usage in _app.tsx:
 * <ExportProvider>
 *   <YourAppComponents />
 * </ExportProvider>
 */
export function ExportProvider({ children }: ExportProviderProps) {
  // Create ONE hook instance at provider level
  // All descendant components access this SAME instance
  const exportState = useExportState();

  return (
    <ExportContext.Provider value={exportState}>
      {children}
    </ExportContext.Provider>
  );
}

/**
 * useExportContext - Use this hook to access export state
 * 
 * Must be called within ExportProvider
 * 
 * Usage:
 * const { activeExports, startExport } = useExportContext();
 */
export function useExportContext(): ExportContextType {
  const context = React.useContext(ExportContext);
  if (!context) {
    throw new Error('useExportContext must be used within ExportProvider');
  }
  return context;
}

